<template>
  <v-container fluid>
    <v-card flat class="mx-4 mb-3 backgroundWhite" :loading="is_loading">
      <div class="d-flex justify-space-between">
        <v-card-title class="text-h6 titleLight--text">Devices Overview</v-card-title>
        <v-spacer />
        <v-card v-if="$store.state.tenant_config.show_planner_group" class="ma-3 backgroundWhite" flat>
          <PlannerGroupPicker
            :items="planner_groups_list"
            :is_loading="is_loading"
            :current_planner_group="current_planner_group"
            @updatePlannerGroup="get_updated_stats($event, 'planner_group')"
          />
        </v-card>
        <v-card class="ma-3 backgroundWhite" flat>
          <DatePickerVT @updatePeriod="get_updated_stats($event, 'time_frame')" :is_loading="is_loading" />
        </v-card>
      </div>
    </v-card>

    <v-row class="common-card-row">
      <v-col cols="12" md="4" class="pa-0 pr-md-3">
        <v-col class="common-card-col">
          <RadialChartVT
            v-if="devices_overview_data && faults_data"
            :title="page_content.faults_detected.title"
            :tooltip="page_content.faults_detected.tooltip"
            :chartData="faults_data"
            @on-click="apply_graph_filter('faults_data', $event)"
          />
          <v-skeleton-loader v-else class="mx-auto" type="image"></v-skeleton-loader>
        </v-col>
      </v-col>
      <v-col cols="12" md="4" class="pa-0 pr-md-3">
        <v-col class="common-card-col">
          <RadialChartVT
            v-if="devices_overview_data && quality_issues"
            :tooltip="page_content.power_quality_issues.tooltip"
            :title="page_content.power_quality_issues.title"
            :chartData="quality_issues"
            @on-click="apply_graph_filter('quality_issues', $event)"
          />
          <v-skeleton-loader v-else class="mx-auto" type="image"></v-skeleton-loader>
        </v-col>
      </v-col>
      <v-col cols="12" md="4" class="pa-0">
        <v-col class="common-card-col">
          <RadialChartVT
            v-if="devices_overview_data && compliance_issues"
            :title="page_content.compliance_issues.title"
            :subtitle="page_content.compliance_issues.subtitle"
            :tooltip="page_content.compliance_issues.tooltip"
            :chartData="compliance_issues"
            @on-click="apply_graph_filter('compliance_issues', $event)"
            :description="page_content.compliance_issues.description"
          />
          <v-skeleton-loader v-else class="mx-auto" type="image"></v-skeleton-loader>
        </v-col>
      </v-col>
    </v-row>

    <v-row class="common-card-row">
      <v-col cols="12" md="6" class="pa-0 pb-3">
        <v-col class="common-card-col" cols="12">
          <v-card flat color="transparent">
            <v-card-title class="titleLight--text text-h6">
              <v-row>
                <v-col cols="4">Devices</v-col>
                <v-spacer class="d-none d-sm-flex"></v-spacer>
                <v-col cols="auto">
                  <v-checkbox
                    class="mr-4"
                    :disabled="!!current_filter"
                    dense
                    v-model="only_installed"
                    label="Only Installed"
                  ></v-checkbox>
                </v-col>
              </v-row>
            </v-card-title>
            <v-card-text class="pa-0">
              <DataTableVT
                :headers="page_content.devices_table"
                :items_list="filtered_devices"
                item_key="DeviceID"
                @rowClick="open_details($event)"
                @add-to-list="watchlist_update($event)"
                @action="table_actions($event)"
                :loading="is_loading"
              />
            </v-card-text>
          </v-card>
        </v-col>
      </v-col>
      <v-col cols="12" md="6" class="pl-0 pl-md-3 pr-0 pt-0">
        <v-col class="common-card-col" cols="12">
          <MapsVT
            v-if="map_data && map_data.length > 0"
            :legends="$config.map_scavenger_legends"
            :options="page_content.maps_config"
            :infoWindowItemsConfig="page_content.infoWindowItemsConfig"
            :items="map_data"
            height="80vh"
            :clusterMarkers="clusterMarkers"
            :liveStream="liveStream"
          />
          <v-skeleton-loader v-else class="mx-auto" type="image"></v-skeleton-loader>
        </v-col>
      </v-col>
    </v-row>

    <CustomOverlay
      v-if="dialog"
      show_header
      :open_as_page="overlay_type == 'details'"
      :open_as_page_url="`/scavenger/${deviceId}`"
      :no_content_padding="overlay_type == 'details'"
      width="80vw"
      @close="closeDialog"
    >
      <template v-slot:content>
        <ScavengerAsset :deviceId="deviceId" v-if="overlay_type == 'details'" />
        <EditScvDetails
          @cancel="dialog = false"
          @save="update_assets_details($event)"
          :asset_data="asset_data"
          v-if="overlay_type == 'edit'"
        />
      </template>
    </CustomOverlay>

    <v-snackbar :color="message_type == 'error' ? 'error' : 'borderDark'" v-model="snackbar" timeout="2000">
      <v-icon v-if="message_type == 'error'">mdi-alert-circle</v-icon>
      {{ error_message }}</v-snackbar
    >
  </v-container>
</template>

<script>
import ScavengerAsset from "../views/ScavengerAsset";
import DataTableVT from "../components/DataTableVT";
import MapsVT from "../components/MapsVT";
import RadialChartVT from "../components/RadialChartVT";
import EditScvDetails from "../components/EditScvDetails";
import DatePickerVT from "../components/DatePickerVT.vue";
import PlannerGroupPicker from "../components/PlannerGroupPicker.vue";
import CustomOverlay from "../components/CustomOverlay.vue";

import { axios_get, axios_post, axios_delete } from "../services/vt_service";
import { scv } from "../services/powerdata";
import { tracking } from "../services/tracking";
import { content } from "../../config/scavenger.js";
import { content as h2Content } from "../../config/scavenger_h2.js";
import { redirect_to_asset } from "../helpers/helpers";
import { scavenger_grpc_services } from "../services/ScavengerServices";

export default {
  name: "ScavengerAssets",
  components: {
    ScavengerAsset,
    DataTableVT,
    MapsVT,
    RadialChartVT,
    EditScvDetails,
    DatePickerVT,
    PlannerGroupPicker,
    CustomOverlay,
  },
  data: function () {
    return {
      filter_conditions: {},
      primary_filter_conditions: "",
      deviceId: null,
      dialog: false,
      search: "",
      page_content: null,
      network_error: false,
      devices: [],
      error_message: "",
      items: ["Today", "This Week", "This Month"],
      devices_overview_data: {},
      watchlist: [],
      only_installed: true,
      filter_communicated: "",
      duration: 1,
      overlay_type: "",
      asset_data: null,
      snackbar: false,
      show_more: false,
      is_loading: false,
      clusterMarkers: true,
      current_filter: null,
      filtering_category: {},
      data_call_interval: null,
      scavenger_list_stream: null,
      current_planner_group: "All",
      liveStream: true,
      message_type: null,
    };
  },
  created() {
    this.page_content = this.$store.state.tenant_config.powerdata_type == "h2scan" ? h2Content : content;
    this.getDetails();
    this.$store.commit("update_page_title", "Power Quality");
  },
  methods: {
    async getDetails() {
      this.network_error = false;
      this.is_loading = true;
      await this.get_device_list();
      await this.get_updated_stats();
    },
    closeDialog() {
      this.dialog = false;
      this.deviceId = null;
    },
    async get_updated_stats(e, type) {
      const time_frame = { duration: 1 };
      if (e && type == "planner_group") this.current_planner_group = e;
      if (type == "time_frame") {
        Object.assign(time_frame, e);
        this.liveStream = e.liveStream;
      }
      if (!e && !type) this.liveStream = false;
      let planner_group =
        !this.current_planner_group || this.current_planner_group == "All" ? null : this.current_planner_group;
      await this.get_scv_donut_data(time_frame, planner_group);
      this.is_loading = false;
    },
    async update_assets_details(data) {
      this.dialog = false;
      this.network_error = false;
      await axios_post(scv.update_scv_details, data, { DeviceID: data.DeviceID });
      this.message_type = "success";
      this.error_message = "Device Details Successfully Saved!";
      this.devices = this.devices.map((elem) => {
        if (elem.DeviceID == data.DeviceID) {
          elem.TransformerName = data.TransformerName;
          elem.AssetID = data.AssetID;
          elem.TransformerNo = data.TransformerNo;
          elem.TransformerRating = data.TransformerRating;
          elem.Latitude = data.Latitude;
          elem.Longitude = data.Longitude;
          elem.Comment = data.Comment;
        }
        return elem;
      });
      this.snackbar = true;
    },
    apply_graph_filter(type, value) {
      this.liveStream = false;
      this.current_filter = value ? { type, value } : null;
      this.only_installed = true;
    },
    table_actions(assets_obj) {
      if (assets_obj.action === "OpenAsAPage") {
        redirect_to_asset(this.$router, "scavenger", assets_obj.item.DeviceID);
      }
      if (assets_obj.action === "EditDetails") {
        if (this.is_readonly_user) {
          this.message_type = "error";
          this.error_message = "You are not authorized to edit the details!";
          this.snackbar = true;
          return;
        }
        this.asset_data = assets_obj;
        this.overlay_type = "edit";
        this.dialog = true;
      }
    },
    batteryIndicator(batteryStatus) {
      return batteryStatus == "High"
        ? "success--text"
        : batteryStatus == "Medium"
        ? "secondary--text"
        : batteryStatus == "Low"
        ? "error--text"
        : "primarGrey--text";
    },
    open_details(item) {
      if (item.ctrlKey) return redirect_to_asset(this.$router, "scavenger", item.DeviceID);
      this.overlay_type = "details";
      this.deviceId = item.DeviceID;
      this.dialog = true;
    },
    watchlist_update(item) {
      item["IsWatchlisted"] ? this.delete_from_watchlist(item) : this.add_to_watchlist(item);
    },
    async add_to_watchlist(item) {
      var data = { DeviceID: item["DeviceID"] };
      this.network_error = false;
      await axios_post(tracking.add_to_watchlist, data);
      item["IsWatchlisted"] = true;
      this.watchlist.push(item["DeviceID"]);
    },
    async delete_from_watchlist(item) {
      var data = { DeviceID: item["DeviceID"] };
      this.network_error = false;
      await axios_delete(tracking.delete_from_watchlist, {}, data);
      this.watchlist.splice(self.watchlist.indexOf(item["DeviceID"]), 1);
      item["IsWatchlisted"] = false;
    },
    async get_device_list() {
      this.network_error = false;
      // let response1 = await axios_get(scv.get_scavenger_list);
      // console.log("scavengerdata restapi: ", response1);
      let response = await scavenger_grpc_services.GetScavengerList();
      this.devices = response;
      this.aggregation_period = 63000;
      this.is_loading = false;
      this.scavenger_list_stream = scavenger_grpc_services.GetScavengerListStream(this.aggregation_period);
      this.scavenger_list_stream.on("data", (response) => {
        this.devices = response.toObject().deviceDataList;
        if (this.liveStream) this.get_updated_stats();
      });
    },
    async get_scv_donut_data(time_frame, planner_group) {
      this.network_error = false;
      // let params = null;
      // if (planner_group) params = { planner_group };
      // const response1 = await axios_get(scv.get_scv_donut_data, { duration }, params);
      const response = await scavenger_grpc_services.GetScvDonutData(time_frame, planner_group ? planner_group : null);
      this.devices_overview_data = response.scvDonutData;
    },
    async get_watchlisted_devices(devices) {
      this.network_error = false;
      let response = await axios_get(tracking.get_device_watchlist);
      this.watchlist =
        response &&
        response.map((elem) => {
          return elem["DeviceID"];
        });
      this.devices = devices.map((elem) => {
        elem.IsWatchlisted = this.watchlist.indexOf(elem.DeviceID) != -1 ? true : false;
        return elem;
      });
    },
    format_chart_data(chart_data, config, type) {
      var data = [];
      this.filtering_category[type] = {};
      var others_total = 0;
      if (chart_data) {
        data = config.data.map((currentValue) => {
          others_total = 0;
          var total = 0;
          var result = chart_data.filter((data) => {
            total = total + data.Total;
            if (
              data.name &&
              !currentValue.values &&
              data.name.toLowerCase().trim() == currentValue.value.toLowerCase().trim()
            ) {
              this.filtering_category[type][currentValue.label] = currentValue.value.toUpperCase().trim();
              return true;
            } else if (data.name && !currentValue.value && currentValue.values) {
              others_total =
                others_total +
                (data.name && currentValue.values.indexOf(data.name.toLowerCase()) == -1 ? data.Total : 0);
              return true;
            } else {
              return false;
            }
          });
          var value = result[0] && result[0]["Total"];
          var final_val = currentValue.label == "Others" ? others_total : value;
          return [
            currentValue.label,
            final_val,
            {
              msg: currentValue.msg,
              title: currentValue.label,
              footer: `${this.$numberWithCommas(final_val)}(${this.$numberWithCommas(
                Math.round((final_val / total) * 10000) / 100
              )}%)`,
            },
          ];
        });
      } else {
        data = [config.header];
      }
      return data ? [config.header, ...data] : null;
    },
  },
  computed: {
    user_account: function () {
      return this.$auth.user;
    },
    filtered_devices: function () {
      return this.only_installed || this.current_filter || this.current_planner_group
        ? this.devices
            .filter((e) => (this.only_installed ? e.DeviceStatus == "Installed" : true))
            .filter((e) => {
              if (!this.current_filter || !this.current_filter.value) return true;
              let value = this.filtering_category[this.current_filter.type][this.current_filter.value];
              if (value == "OK")
                return (
                  this.filter_devices[this.current_filter.type]["all"] &&
                  this.filter_devices[this.current_filter.type]["all"].indexOf(e.DeviceID) == -1
                );
              return (
                this.filter_devices[this.current_filter.type][value] &&
                this.filter_devices[this.current_filter.type][value].indexOf(e.DeviceID) != -1
              );
            })
            .filter((e) => {
              if (!this.current_planner_group || this.current_planner_group == "All") return true;
              return this.device_planner_groups[e.DeviceID] == this.current_planner_group;
            })
        : this.devices;
    },
    filtered_devices_count: function () {
      let temp = this.devices.filter((e) => e.DeviceStatus == "Installed");
      return temp.length;
    },
    map_data: function () {
      return this.filtered_devices
        ? this.filtered_devices.filter((elem) => {
            return elem.CurrentLat && elem.CurrentLng ? true : false;
          })
        : null;
    },
    filter_devices: function () {
      let temp = {};
      this.devices_overview_data &&
        Object.keys(this.devices_overview_data).map((elm) => {
          const type =
            elm == "faultsData" ? "faults_data" : elm == "qualityIssues" ? "quality_issues" : "compliance_issues";
          temp[type] = {};
          this.devices_overview_data[elm].map((e) => {
            temp[type][e.name] = e.devices;
            temp[type]["all"] = temp[type]["all"] ? temp[type]["all"] : [];
            e.devices &&
              e.devices.map((d) => {
                if (temp[type]["all"].indexOf(d) == -1) temp[type]["all"].push(d);
              });
          });
        });
      return temp;
    },
    faults_data: function () {
      let faults = [];
      this.devices_overview_data &&
        this.devices_overview_data["faultsData"] &&
        this.devices_overview_data["faultsData"].length > 0 &&
        this.devices_overview_data["faultsData"].map((e) => {
          faults.push({ name: e.name, Total: e.total });
        });
      return faults ? this.format_chart_data(faults, this.page_content.faults_detected, "faults_data") : [];
    },
    quality_issues: function () {
      let quality_issues = [];
      this.devices_overview_data &&
        this.devices_overview_data["qualityIssues"] &&
        this.devices_overview_data["qualityIssues"].length > 0 &&
        this.devices_overview_data["qualityIssues"].map((e) => {
          quality_issues.push({ name: e.name, Total: e.total });
        });
      return quality_issues
        ? this.format_chart_data(quality_issues, this.page_content.power_quality_issues, "quality_issues")
        : [];
    },
    compliance_issues: function () {
      let compliance_issues = [];
      this.devices_overview_data &&
        this.devices_overview_data["complianceIssues"] &&
        this.devices_overview_data["complianceIssues"].length > 0 &&
        this.devices_overview_data["complianceIssues"].map((e) => {
          compliance_issues.push({ name: e.name, Total: e.total });
        });
      return compliance_issues
        ? this.format_chart_data(compliance_issues, this.page_content.compliance_issues, "compliance_issues")
        : [];
    },
    device_planner_groups: function () {
      let planner_groups = {};
      this.devices &&
        this.devices.map((e) => {
          if (e.DeviceStatus == "Installed") planner_groups[e.DeviceID] = e.PlannerGroup;
        });
      return planner_groups;
    },
    planner_groups_list: function () {
      let planner_groups = ["All"];
      this.device_planner_groups &&
        Object.keys(this.device_planner_groups).map((e) => {
          let pg = this.device_planner_groups[e];
          if (pg && planner_groups.indexOf(pg) == -1) planner_groups.push(pg);
        });
      return planner_groups;
    },
    is_readonly_user: function () {
      const ROLE_KEY = process.env["VUE_APP_ROLE_KEY"];
      let role = this.$auth.user[ROLE_KEY] && this.$auth.user[ROLE_KEY];
      return role && role.length > 0 && role.indexOf("ReadOnlyUser") != -1 ? true : false;
    },
  },
  beforeDestroy() {
    // Clear interval when component is destroyed
    // clearInterval(this.data_call_interval);
    this.scavenger_list_stream.cancel();
  },
};
</script>
<style lang="scss" scoped>
::v-deep .right-border {
  // border-right: none;
  border-right: 1px solid var(--v-border-base);
}

::v-deep .fxd-height {
  min-height: 50px;
  align-items: flex-start;
}

.toggle-section {
  position: absolute;
  z-index: 9;
  top: 80px;
  right: 10px;
  cursor: pointer;
}

::v-deep .planner-group.v-text-field .v-input__control .v-input__slot .v-input__append-inner {
  margin-top: 0.5em;
}

::v-deep .planner-group.v-text-field .v-input__control .v-input__slot .v-select__slot {
  height: auto !important;
}
</style>
